import type { GeoJsonFeatureCollection, MapHitEvent, MapMarker } from '@gpx-spa/geo-domain'
import type { MapInteractionController, MapSurfaceHost } from '../shared/map-interaction-controller.js'
import { countClusterEntities } from '../shared/marker-cluster-color.js'
import { htmlFromMapSurface } from '../shared/surface-content-to-html.js'
import { leafletMarkerMetadata } from './leaflet-marker-metadata.js'
import L from 'leaflet'
import type { MarkerCluster } from 'leaflet.markercluster'

function clusterHitPayloadFromLeafletCluster(cluster: MarkerCluster): {
  pointCount: number
  childMarkerIds?: string[]
} {
  const childMarkers = cluster.getAllChildMarkers()
  const mapMarkers: MapMarker[] = []
  for (const cm of childMarkers) {
    const meta = leafletMarkerMetadata.get(cm)
    if (meta) mapMarkers.push(meta)
  }
  const entityCount = countClusterEntities(mapMarkers)
  const childMarkerIds = mapMarkers.map((m) => m.id)
  return {
    pointCount: entityCount,
    childMarkerIds: childMarkerIds.length ? childMarkerIds : undefined,
  }
}

export function createLeafletSurfaceHost(leafletMap: L.Map): MapSurfaceHost {
  return {
    showPopup(latlng, surface) {
      leafletMap.closePopup()
      const html = htmlFromMapSurface(surface)
      const offset = surface.anchor?.offset
      L.popup({
        closeButton: surface.dismiss?.closeButton !== false,
        closeOnClick: surface.dismiss?.closeOnMapClick !== false,
        maxWidth: surface.anchor?.maxWidth ? parseInt(surface.anchor.maxWidth, 10) || 320 : 320,
        ...(offset ? { offset: L.point(offset[0], offset[1]) } : {}),
      })
        .setLatLng(L.latLng(latlng.lat, latlng.lng))
        .setContent(html)
        .openOn(leafletMap)
    },
    showTooltip(latlng, surface) {
      leafletMap.closePopup()
      const html = htmlFromMapSurface(surface)
      const offset = surface.anchor?.offset ?? [0, -8]
      L.popup({
        closeButton: false,
        closeOnClick: false,
        autoClose: true,
        className: 'gpx-map-tooltip-surface',
        maxWidth: surface.anchor?.maxWidth ? parseInt(surface.anchor.maxWidth, 10) || 280 : 280,
        offset: L.point(offset[0], offset[1]),
      })
        .setLatLng(L.latLng(latlng.lat, latlng.lng))
        .setContent(html)
        .openOn(leafletMap)
    },
    close() {
      leafletMap.closePopup()
    },
    setCursorPointer(on) {
      leafletMap.getContainer().style.cursor = on ? 'pointer' : ''
    },
  }
}

function latLngFromLeaflet(e: L.LeafletMouseEvent): { lat: number; lng: number } {
  return { lat: e.latlng.lat, lng: e.latlng.lng }
}

function bindPointerEvents(
  layer: L.Layer,
  controller: MapInteractionController,
  buildHit: (trigger: 'click' | 'hover', e: L.LeafletMouseEvent) => MapHitEvent,
): void {
  layer.on('click', (e: L.LeafletMouseEvent) => {
    L.DomEvent.stopPropagation(e)
    void controller.dispatchHit(buildHit('click', e))
  })
  layer.on('mouseover', (e: L.LeafletMouseEvent) => {
    controller.pointerEnter()
    void controller.dispatchHit(buildHit('hover', e))
  })
  layer.on('mouseout', () => {
    controller.scheduleHoverLeave()
  })
}

export function bindLeafletMarkerInteractions(
  layerId: string,
  lm: L.Marker,
  marker: MapMarker,
  controller: MapInteractionController,
): void {
  bindPointerEvents(lm, controller, (trigger, e) => ({
    trigger,
    latlng: latLngFromLeaflet(e),
    nativeEvent: e,
    target: {
      type: 'marker',
      layerId,
      marker,
      position: marker.position,
    },
  }))
}

export function bindLeafletPathInteractions(
  layerId: string,
  path: L.Layer,
  feature: GeoJsonFeatureCollection['features'][number],
  controller: MapInteractionController,
): void {
  bindPointerEvents(path, controller, (trigger, e) => ({
    trigger,
    latlng: latLngFromLeaflet(e),
    nativeEvent: e,
    target: {
      type: 'feature',
      layerId,
      feature,
      position: latLngFromLeaflet(e),
    },
  }))
}

export function bindLeafletClusterGroupInteractions(
  layerId: string,
  group: L.MarkerClusterGroup,
  controller: MapInteractionController,
  leafletMap: L.Map,
): void {
  group.on('clusterclick', (e: L.LeafletMouseEvent & { layer: MarkerCluster }) => {
    const cluster = e.layer
    const latlng = cluster.getLatLng()
    const position = { lat: latlng.lat, lng: latlng.lng }
    void controller.dispatchHit({
      trigger: 'click',
      latlng: position,
      nativeEvent: e,
      target: {
        type: 'cluster',
        layerId,
        position,
        cluster: clusterHitPayloadFromLeafletCluster(cluster),
      },
    })
    if (controller.clusterClickPolicy() === 'expand') {
      const bounds = cluster.getBounds()
      if (bounds.isValid()) {
        leafletMap.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 })
      }
    }
  })

  group.on('clustermouseover', (e: L.LeafletMouseEvent & { layer: MarkerCluster }) => {
    const cluster = e.layer
    const latlng = cluster.getLatLng()
    const position = { lat: latlng.lat, lng: latlng.lng }
    controller.pointerEnter()
    void controller.dispatchHit({
      trigger: 'hover',
      latlng: position,
      nativeEvent: e,
      target: {
        type: 'cluster',
        layerId,
        position,
        cluster: clusterHitPayloadFromLeafletCluster(cluster),
      },
    })
  })

  group.on('clustermouseout', () => {
    controller.scheduleHoverLeave()
  })
}
